import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { differenceInCalendarDays, format, startOfDay } from "date-fns"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDateTime(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatDuration(seconds) {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatMinutes(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export function formatRelativeTime(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  
  return formatDate(dateString);
}

/**
 * Human-friendly labels for call/message lists: "1 hr back", "Yesterday", weekday name, "2 days back", etc.
 */
export function formatListActivityTime(dateString) {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) return formatDateTime(dateString);

  const calDays = differenceInCalendarDays(startOfDay(now), startOfDay(d));

  if (calDays === 0) {
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min back`;
    const hrs = Math.floor(diffMs / 3600000);
    return hrs === 1 ? '1 hr back' : `${hrs} hr back`;
  }
  if (calDays === 1) return 'Yesterday';
  if (calDays === 2) return '2 days back';
  if (calDays >= 3 && calDays <= 6) return format(d, 'EEEE');
  if (calDays < 14) return `${calDays} days back`;
  return format(d, 'd MMM yyyy');
}

/** Returns `tel:…` href for click-to-dial, or null if not dialable. */
export function buildTelHref(phone) {
  if (phone == null) return null;
  const s = String(phone).trim();
  if (!s || /^unknown number$/i.test(s)) return null;
  const digits = s.replace(/[^\d+]/g, '');
  if (!digits || digits === '+') return null;
  return `tel:${digits}`;
}
